import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { saasApi } from '../../utils/saasApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import DrawerShell from '../../components/DrawerShell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Plus, UserPlus, Loader2, Copy, Check, CheckCircle2, Globe, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function tenantLoginUrl(t, cfg) {
  // Prefer custom domain → configured app base domain (subdomain mode) →
  // browser-origin fallback (?tenant= query param).
  if (t?.allowed_domains?.length) return `https://${t.allowed_domains[0]}/login`;
  if (cfg?.app_base_domain) {
    const scheme = cfg.scheme || 'https';
    return `${scheme}://${t.slug}.${cfg.app_base_domain}/login`;
  }
  const origin = (typeof window !== 'undefined' && window.location.origin) || '';
  return `${origin}/login?tenant=${t.slug}`;
}

function slugifyName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <div className="flex items-stretch border rounded-md overflow-hidden bg-white">
        <input readOnly value={value || ''}
          className="flex-1 px-3 py-2 text-sm font-mono text-zinc-900 truncate"
          data-testid={testid} />
        <button onClick={copy} type="button"
          className="px-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs flex items-center gap-1.5"
          data-testid={`${testid}-copy`}>
          {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function HandoffDialog({ payload, open, onClose }) {
  if (!payload) return null;
  const welcome = (
`Hi ${payload.admin_name || 'there'},

Your ${payload.tenant_name} workspace on eCare 360 is ready 🎉

🔗 Sign in here: ${payload.login_url}
👤 Email:        ${payload.admin_email}
🔑 Temporary password: ${payload.admin_password}

You'll be asked to set a permanent password after first sign-in.

— Sent via eCare 360`);
  const copyWelcome = () => {
    navigator.clipboard?.writeText(welcome).then(() => toast.success('Welcome message copied'));
  };
  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      eyebrow="Tenant Provisioned"
      title="Share these credentials"
      subtitle="Shown once — copy and send to your customer now"
      accent="#059669"
      icon={<CheckCircle2 className="h-5 w-5" />}
      width="md"
      testId="sa-tenant-handoff-dialog"
      footer={
        <>
          <Button variant="outline" onClick={copyWelcome} data-testid="sa-handoff-copy-welcome">
            <Copy className="h-4 w-4 mr-2" /> Copy welcome message
          </Button>
          <Button onClick={onClose} className="bg-amber-600 hover:bg-amber-700" data-testid="sa-handoff-done">
            Done
          </Button>
        </>
      }
    >
      <div className="p-5 space-y-4">
        <CopyField label="Sign-in URL" value={payload.login_url} testid="sa-handoff-url" />
        <div className="grid grid-cols-2 gap-3">
          <CopyField label="Admin email" value={payload.admin_email} testid="sa-handoff-email" />
          <CopyField label="Temporary password" value={payload.admin_password} testid="sa-handoff-password" />
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[12px] text-blue-900">
          <Globe className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          When the customer opens the link, the clinic picker is hidden and
          they see only their branded login screen.
        </div>
      </div>
    </DrawerShell>
  );
}

function CreateTenantDialog({ plans, onCreated, platformCfg }) {
  const [open, setOpen] = useState(false);
  const [clinicTypes, setClinicTypes] = useState([]);
  const [form, setForm] = useState({
    slug: '',
    name: '',
    admin_email: '',
    admin_password: '',
    admin_name: 'Clinic Admin',
    isolation_mode: 'shared',
    plan_code: 'free',
    max_users: '',
    clinic_type: 'reception_pharmacy',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load clinic-type catalog once (public endpoint, no auth needed).
  useEffect(() => {
    axios.get(`${API}/api/tenant/clinic-types`)
      .then(({ data }) => setClinicTypes(data || []))
      .catch(() => setClinicTypes([]));
  }, []);

  const selectedClinicType = clinicTypes.find(c => c.code === form.clinic_type);

  const selectedPlan = plans.find(p => p.code === form.plan_code);
  const planUserLimit = (selectedPlan?.limits || {}).users;
  // Auto-fill max_users from plan when plan changes (unless admin already typed one)
  useEffect(() => {
    if (!form.max_users && planUserLimit !== undefined) {
      setForm(f => ({ ...f, max_users: planUserLimit === -1 ? '' : String(planUserLimit) }));
    }
    // eslint-disable-next-line
  }, [form.plan_code]);

  // The URL the new tenant will use — preview reflects the active platform
  // config: subdomain mode if APP_BASE_DOMAIN is set, otherwise ?tenant=.
  const baseDomain = platformCfg?.app_base_domain || '';
  const scheme = platformCfg?.scheme || 'https';
  const previewUrl = form.slug
    ? (baseDomain
        ? `${scheme}://${form.slug}.${baseDomain}/login`
        : `${(typeof window !== 'undefined' && window.location.origin) || ''}/login?tenant=${form.slug}`)
    : '';
  const hostHint = baseDomain ? `.${baseDomain}` : `.${(typeof window !== 'undefined' && window.location.hostname) || 'your-app.com'}`;

  // Auto-derive subdomain from clinic name (unless user typed one manually)
  useEffect(() => {
    if (!slugTouched && form.name) {
      setForm((f) => ({ ...f, slug: slugifyName(f.name) }));
    }
  }, [form.name, slugTouched]);

  const submit = async () => {
    if (!form.slug || !form.name || !form.admin_email || !form.admin_password) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.admin_password.length < 8) {
      toast.error('Temporary password must be at least 8 characters'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert max_users (empty = inherit plan; numeric = override)
      if (payload.max_users === '' || payload.max_users === null) {
        delete payload.max_users;
      } else {
        payload.max_users = parseInt(payload.max_users, 10);
      }
      const data = await saasApi.createTenant(payload);
      setOpen(false);
      onCreated(data);   // bubbles handoff payload back up
      setSlugTouched(false);
      setForm(f => ({
        slug: '', name: '', admin_email: '', admin_password: '',
        admin_name: 'Clinic Admin', isolation_mode: 'shared', plan_code: 'free', max_users: '',
        clinic_type: 'reception_pharmacy',
      }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create tenant');
    }
    setSaving(false);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="sa-new-tenant-btn">
        <Plus className="h-4 w-4 mr-2" /> New tenant
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Provision"
        title="Onboard a new tenant"
        subtitle="Pick a subdomain — it's the unique URL where the clinic's staff will sign in"
        accent="#d97706"
        icon={<Building2 className="h-5 w-5" />}
        width="lg"
        testId="sa-new-tenant-dialog"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}
              className="bg-amber-600 hover:bg-amber-700" data-testid="sa-tenant-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create &amp; show credentials
            </Button>
          </>
        }
      >
        <div className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-name">Clinic / display name *</Label>
            <Input id="t-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Family Clinic" data-testid="sa-tenant-name" />
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-slug" className="flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5 text-zinc-500" />
              Subdomain *
            </Label>
            <div className="flex items-stretch border rounded-md overflow-hidden">
              <Input id="t-slug" value={form.slug}
                onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugifyName(e.target.value) }); }}
                placeholder="acme-clinic"
                className="border-0 rounded-none focus-visible:ring-0"
                data-testid="sa-tenant-slug" />
              <div className="px-3 flex items-center bg-zinc-50 text-zinc-500 text-xs border-l">
                {hostHint}
              </div>
            </div>
            {form.slug && (
              <p className="text-[11px] text-zinc-500 break-all" data-testid="sa-tenant-url-preview">
                Login URL: <span className="font-mono text-zinc-700">{previewUrl}</span>
              </p>
            )}
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-clinic-type">Clinic type *</Label>
            <Select value={form.clinic_type}
              onValueChange={(v) => setForm({ ...form, clinic_type: v })}>
              <SelectTrigger data-testid="sa-tenant-clinic-type"><SelectValue placeholder="Pick a clinic configuration" /></SelectTrigger>
              <SelectContent>
                {clinicTypes.map(c => (
                  <SelectItem key={c.code} value={c.code} data-testid={`sa-clinic-type-${c.code}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedClinicType && (
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                Staff roles enabled:{' '}
                <span className="font-mono text-zinc-700" data-testid="sa-tenant-clinic-roles">
                  {selectedClinicType.allowed_roles.join(', ')}
                </span>
              </p>
            )}
          </div>

          <div className="col-span-1 space-y-1.5">
            <Label htmlFor="t-iso">Data isolation</Label>
            <Select value={form.isolation_mode}
              onValueChange={(v) => setForm({ ...form, isolation_mode: v })}>
              <SelectTrigger data-testid="sa-tenant-isolation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="shared">Shared DB (cost-efficient)</SelectItem>
                <SelectItem value="dedicated">Dedicated DB (HIPAA-friendly)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1 space-y-1.5">
            <Label htmlFor="t-plan">Plan</Label>
            <Select value={form.plan_code}
              onValueChange={(v) => setForm({ ...form, plan_code: v, max_users: '' })}>
              <SelectTrigger data-testid="sa-tenant-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.code} value={p.code}>
                    {p.name} (${p.price_monthly}/mo)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-maxusers" className="flex items-center justify-between">
              <span>Max users (override)</span>
              <span className="text-[11px] font-normal text-zinc-500">
                Plan default: <b>{planUserLimit === -1 ? 'Unlimited' : (planUserLimit ?? '—')}</b>
              </span>
            </Label>
            <Input id="t-maxusers" value={form.max_users}
              onChange={(e) => setForm({ ...form, max_users: e.target.value.replace(/[^0-9]/g, '') })}
              placeholder="Leave blank to inherit from plan"
              data-testid="sa-tenant-max-users" />
            <p className="text-[11px] text-zinc-500">
              Set higher than the plan to grant a custom allowance; set lower
              to cap a free trial.
            </p>
          </div>

          <div className="col-span-2 pt-2 border-t">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2.5 mt-1">
              Primary user (clinic admin)
            </div>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-admin-name">Full name</Label>
            <Input id="t-admin-name" value={form.admin_name}
              onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
              data-testid="sa-tenant-admin-name" />
          </div>
          <div className="col-span-1 space-y-1.5">
            <Label htmlFor="t-admin-email">Email *</Label>
            <Input id="t-admin-email" type="email" value={form.admin_email}
              onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
              placeholder="admin@acme.com" data-testid="sa-tenant-admin-email" />
          </div>
          <div className="col-span-1 space-y-1.5">
            <Label htmlFor="t-admin-pass">Temporary password *</Label>
            <Input id="t-admin-pass" type="text" value={form.admin_password}
              onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
              placeholder="Min 8 chars" data-testid="sa-tenant-admin-pass" />
          </div>
        </div>
      </DrawerShell>
    </>
  );
}

function InviteUserDialog({ tenant, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'doctor', password: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error('Email, name and password are required'); return;
    }
    setSaving(true);
    try {
      await saasApi.inviteUser(tenant.slug, form);
      toast.success(`Invited ${form.email}`);
      setOpen(false);
      onDone && onDone();
      setForm({ email: '', name: '', role: 'doctor', password: '' });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to invite');
    }
    setSaving(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`sa-invite-${tenant.slug}`}>
        <UserPlus className="h-4 w-4 mr-1" /> Invite user
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Team Invite"
        title={`Invite user to ${tenant.name}`}
        subtitle="They'll sign in with the temporary password"
        accent="#d97706"
        icon={<UserPlus className="h-5 w-5" />}
        width="md"
        testId="sa-invite-user-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}
              className="bg-amber-600 hover:bg-amber-700" data-testid="sa-invite-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Invite
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-3">
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="sa-invite-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="sa-invite-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="sa-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="doctor">Doctor</SelectItem>
                <SelectItem value="receptiondesk">Reception Desk</SelectItem>
                <SelectItem value="pharmacist">Pharmacist</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="pharmacy">Pharmacy (full module)</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="pharmacystores">Pharmacy Stores</SelectItem>
                <SelectItem value="storesadmin">Stores Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Temporary password *</Label>
            <Input value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="sa-invite-pass" />
          </div>
        </div>
      </DrawerShell>
    </>
  );
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [handoff, setHandoff] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState('');
  const [platformCfg, setPlatformCfg] = useState({ app_base_domain: '', scheme: 'https' });

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, p, cfgRes] = await Promise.all([
        saasApi.listTenants(),
        saasApi.listPlans(),
        axios.get(`${API}/api/platform/config`),
      ]);
      setTenants(t || []);
      setPlans(p || []);
      setPlatformCfg(cfgRes.data || { app_base_domain: '', scheme: 'https' });
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const onCreated = (data) => {
    if (data?.handoff) {
      // Recompute the login URL client-side using platform config (handles
      // ingress-rewrite quirks in preview env + applies subdomain shape in
      // production once APP_BASE_DOMAIN is set).
      const browserUrl = tenantLoginUrl(data, platformCfg);
      setHandoff({ ...data.handoff, login_url: browserUrl });
    }
    refresh();
  };

  const toggleStatus = async (t) => {
    try {
      if (t.status === 'suspended') {
        await saasApi.updateTenant(t.slug, { status: 'active' });
        toast.success(`${t.slug} reactivated`);
      } else {
        await saasApi.suspendTenant(t.slug);
        toast.success(`${t.slug} suspended`);
      }
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  const changePlan = async (t, code) => {
    try {
      await saasApi.updateTenant(t.slug, { plan_code: code });
      toast.success(`Plan updated`);
      refresh();
    } catch (e) {
      toast.error('Failed to update plan');
    }
  };

  const copyLoginUrl = (t) => {
    const url = tenantLoginUrl(t, platformCfg);
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedSlug(t.slug);
      setTimeout(() => setCopiedSlug(''), 1800);
    });
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-bold text-zinc-900"
            style={{ fontFamily: 'Manrope' }}
            data-testid="sa-tenants-title"
          >
            Tenants
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Each tenant gets a unique subdomain URL. Share it with the primary user to sign in.
          </p>
        </div>
        <CreateTenantDialog plans={plans} onCreated={onCreated} platformCfg={platformCfg} />
      </div>

      <div className="mt-6 bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b">
            <tr className="text-left text-zinc-600">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Login URL</th>
              <th className="px-4 py-3 font-medium">Isolation</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-zinc-500">No tenants yet. Click <b>New tenant</b> to create one.</td></tr>
            ) : tenants.map((t) => (
              <tr key={t.id} className="border-b last:border-0 align-middle" data-testid={`sa-tenant-row-${t.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-zinc-900">{t.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{t.slug}</div>
                </td>
                <td className="px-4 py-3 max-w-[260px]">
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-[11px] text-zinc-700 truncate" title={tenantLoginUrl(t, platformCfg)}>
                      {tenantLoginUrl(t, platformCfg)}
                    </span>
                    <button
                      onClick={() => copyLoginUrl(t)}
                      className="text-zinc-500 hover:text-zinc-900 p-1 rounded"
                      title="Copy login URL"
                      data-testid={`sa-tenant-copy-url-${t.slug}`}
                    >
                      {copiedSlug === t.slug
                        ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                        : <Copy className="h-3.5 w-3.5" />}
                    </button>
                    <a
                      href={tenantLoginUrl(t, platformCfg)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-zinc-500 hover:text-zinc-900 p-1 rounded"
                      title="Open in new tab"
                      data-testid={`sa-tenant-open-url-${t.slug}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    t.isolation_mode === 'dedicated'
                      ? 'bg-violet-50 text-violet-700 border-violet-200'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}>
                    {t.isolation_mode}
                  </span>
                  {t.clinic_type && (
                    <span className="block text-[10px] text-zinc-500 mt-1" data-testid={`sa-tenant-clinic-type-${t.slug}`}>
                      {(t.clinic_type === 'solo_doctor' && 'Solo doctor') ||
                       (t.clinic_type === 'reception' && '+ Reception') ||
                       (t.clinic_type === 'reception_pharmacy' && '+ Reception & Pharmacy') ||
                       t.clinic_type}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Select value={t.plan_code} onValueChange={(v) => changePlan(t, v)}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`sa-tenant-plan-${t.slug}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => (
                        <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    t.status === 'suspended'
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  }`}>
                    {t.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <InviteUserDialog tenant={t} onDone={refresh} />
                  <Button variant="outline" size="sm" onClick={() => toggleStatus(t)}
                    data-testid={`sa-tenant-toggle-${t.slug}`}>
                    {t.status === 'suspended'
                      ? <><Play className="h-4 w-4 mr-1" /> Resume</>
                      : <><Pause className="h-4 w-4 mr-1" /> Suspend</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HandoffDialog
        payload={handoff}
        open={!!handoff}
        onClose={() => setHandoff(null)}
      />
    </div>
  );
}
